"use client";
import React, { useState } from "react";
import Image from "next/image";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  formatCompactNumber,
  formatDisplayAmount,
  formatIndianNumber,
} from "@/utils/commonFunctions";

type CalculatorType = "sip" | "lumpsum" | "swp";

interface YearlyReportItem {
  year: number;
  currentValue: number;
}

interface Props {
  investment: number;
  absProfitValue: number;
  futureValue: number;
  yearlyReport: YearlyReportItem[];
}

export default function HomeCalcOutputPart({
  calculation,
  calcType,
}: {
  calculation: Props;
  calcType: CalculatorType;
}) {
  const [isChartLoading, setIsChartLoading] = useState(true);

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "line",
      height: 192,
      backgroundColor: "transparent",
      style: {
        fontFamily: "Inter, sans-serif",
      },
      events: {
        load: () => {
          setIsChartLoading(false);
        },
      },
    },
    title: {
      text: undefined,
    },
    xAxis: {
      categories: calculation.yearlyReport.map((d) => d.year.toString()),
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,

      labels: {
        formatter() {
          return String(this.value);
        },
        style: {
          fontSize: "10px",
          fontWeight: "400",
          color: "#9CA3AF",
        },
      },
    },
    yAxis: {
      min: 0,
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      minorGridLineWidth: 0,
      title: {
        text: undefined,
      },
      labels: {
        style: {
          fontSize: "10px",
          fontWeight: "400",
          color: "#9CA3AF",
        },
        formatter: function () {
          return "₹" + formatCompactNumber(this.value as number, false);
        },
      },
    },
    tooltip: {
      useHTML: true,
      shared: true,
      formatter: function () {
        const resultant = Math.round(this.points?.[0].y as number);
        return `
              <div class="bg-white text-center">
                <div class="font-bold">year ${this.key}</div>
                 
                <div class="font-semibold text-primaryBlue"><span class="font-normal">Resultant Amount:</span> ${formatIndianNumber(Number(resultant), { prefix: "₹" })}</div>
              </div>
            `;
      },
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        type: "area",
        name: "Resultant",
        data: calculation.yearlyReport.map((d) => Math.round(d.currentValue)),
        color: "#1A51AA",
        lineWidth: 2,

        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0.05, "rgba(26, 81, 170, 0.2)"],
            [0.95, "rgba(26, 81, 170, 0)"],
          ],
        },
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 6,
            },
          },
        },
      },
    ],
  };

  return (
    <div className="w-full md:w-1/2 space-y-5">
      <div className="rounded-2xl sm:rounded-4xl 2md:rounded-8xl border border-[#BDD6FF] bg-[#F2F7FF] p-4 sm:p-6">
        <div className="flex justify-between gap-5 mb-5">
          <div
            className="text-left space-y-2"
            title={`₹${formatIndianNumber(calculation.investment)}`}
          >
            <p className="text-sm leading-1.3 text-[#767676]">Total Invested</p>
            <p className="sm:text-xl sm:leading-7 font-medium leading-1.2 text-primaryBlack">
              {formatDisplayAmount(calculation.investment)}
            </p>
          </div>

          <div
            className="text-right space-y-2"
            title={`₹${formatIndianNumber(calculation.absProfitValue)}`}
          >
            <p className="text-sm leading-1.3 text-[#767676]">
              {calcType === "swp" ? "Remaining Value" : "Wealth Gained"}
            </p>
            <p
              className={[
                "sm:text-xl sm:leading-7 font-medium leading-1.2",
                calculation.absProfitValue > 0
                  ? "text-[#1CB900]"
                  : "text-[#FF4D4D]",
              ].join(" ")}
            >
              {calculation.absProfitValue > 0 ? "+" : ""}
              {formatDisplayAmount(calculation.absProfitValue)}
            </p>
          </div>
        </div>

        <div
          className="text-center space-y-2.5"
          title={`₹${formatIndianNumber(calculation.futureValue)}`}
        >
          <div className="text-2.5xl sm:text-4xl 2lg:text-50 font-extrabold text-primaryBlue">
            {formatDisplayAmount(calculation.futureValue)}
          </div>

          <p
            className={[
              "text-sm leading-1.3",
              calcType === "swp" && calculation.absProfitValue < 0
                ? "text-[#ff3939] font-semibold"
                : "text-[#767676]",
            ].join(" ")}
          >
            {calcType === "swp"
              ? calculation.absProfitValue < 0
                ? "*Your investible amount is insufficient to meet your desired withdrawal amount."
                : "Total Withdrawal"
              : "Projected Corpus"}
          </p>
        </div>
      </div>

      <div className="rounded-2xl sm:rounded-4xl 2md:rounded-8xl border border-[#BDD6FF] p-4 sm:p-6 pb-1!">
        <p className="mb-5.5 text-sm sm:text-base leading-1.2! font-semibold text-primaryBlack">
          Wealth Curve
        </p>
        <div className="h-48 relative">
          {isChartLoading && (
            <Image
              src="/next_assets/images/gif/chart_loading.gif"
              unoptimized
              alt="Loading Chart Animation"
              loading="eager"
              width={256}
              height={144}
              priority
              className="h-[70%] block mx-auto"
            />
          )}
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
      </div>
    </div>
  );
}
